import { BaseDatabase, WithAllType, arrayOrCollection } from "../index";

export type ForeignKey<T> = Number | T;

export interface Model
{
    id?: number;
}

export interface Base extends Model
{
    created_at?: Date;
    modified_at?: Date;
}

export interface ExtBase extends Base
{
    ext_id?: string;
}

export class User
{
    private _userSettings;
    private _perm_check_cache: {[perm: string]: boolean} = {};

    id: number;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    is_staff: boolean;
    is_active: boolean;
    is_superuser: boolean;
    date_joined: string;

    public hasPerm(perm: string): boolean
    {
        // Super users has all permissions
        if (this.is_superuser) {
            return true;
        }

        // Check if permission is already cached
        if(!this._perm_check_cache.hasOwnProperty(perm)) {
            if (this._userSettings.permissions) {

                // Module check == True if the user has any permissions in the given package (e.g. tracking)
                let is_module_check = perm.indexOf('.') < 0;
                this._perm_check_cache[perm] = this._userSettings.permissions.some(user_perm => {
                    if (is_module_check) {
                        return user_perm.startsWith(perm)
                    }
                    return user_perm == perm;
                });
            } else {
                this._perm_check_cache[perm] = false;
            }
        }

        return this._perm_check_cache[perm];
    }

    public hasPerms(perm_list): boolean
    {
        return perm_list.every(perm => this.hasPerm(perm));
    }

    public static createFrom(userData, userSettings): User
    {
        let user = new User();
        for (let key in userData) {
            user[key] = userData[key];
        }
        user._userSettings = userSettings
        return user;
    }
}

export interface UserAccountLike extends Base
{
    id: number;
    timezone: string;
    initial_password: boolean;
    business_id?: number;
    location_id?: number;
    user_id: number;
    language: string;
    user?: ForeignKey<User>;
}

export class UserAccount implements UserAccountLike
{
    id: number;
    timezone: string;
    initial_password: boolean;
    business_id?: number;
    location_id?: number;
    user_id: number;
    language: string;
    user?: ForeignKey<User>;

    public static withAll(db: BaseDatabase, targetUserAccounts: WithAllType<UserAccountLike>): PromiseLike<UserAccountLike[]>
    {
        let accounts: UserAccountLike[];

        return arrayOrCollection(targetUserAccounts).then((resolvedUserAccounts) =>
        {
            accounts = resolvedUserAccounts;

            return Promise.all([db.User.where('id').anyOf(accounts.map(o => o.user_id)).toArray()]);
        }).then((results) =>
        {
            let users: User[] = results[0];

            for(let account of accounts)
            {
                account.user = users.find(o => o.id == account.user_id);
            }
        }).then(() =>
        {
            return accounts;
        });
    }
}

export interface TypedModel extends Model
{
    type: string;
}

export interface TrackingObject extends ExtBase, TypedModel
{
    name: string;
    epc: string;
}

export interface AddressLike extends TypedModel
{
    title: string;
    street_address: string;
    street_address2: string;
    region: string;
    postal_code: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
}

export interface LocationLike extends ExtBase, TypedModel
{
    parent?: LocationLike;
    parent_id: number;
    address?: Address;
    address_id: number;
    name: string;
}

export class Location implements Partial<LocationLike>
{
    public static withAll(db: BaseDatabase, targetLocations: WithAllType<LocationLike>): PromiseLike<LocationLike[]>
    {
        let locations: LocationLike[];
        return arrayOrCollection(targetLocations).then((resolvedLocations) =>
        {
            locations = resolvedLocations;

            return Promise.all([
                db.Address.where('id').anyOf(locations.filter(o => o.address_id != null).map(o => o.address_id)).toArray(),
            ]);
        }).then((results) =>
        {
            let addresses: AddressLike[] = results[0];

            for (let location of locations)
            {
                location.address = addresses.find(o => o.id == location.address_id);
            }

        }).then(() =>
        {
            return locations;
        });
    }
}

export class Address implements AddressLike
{
    title: string;
    street_address: string;
    street_address2: string;
    region: string;
    postal_code: string;
    city: string;
    country: string;
    latitude: number;
    longitude: number;
    type: string;

    public static as_text(address: AddressLike, line_breaks = false, line_break = '\n')
    {
        let text = address.street_address
        if (address.street_address2)
        {
            text += line_breaks ? line_break : ' '
            text += address.street_address2
        }

        if (address.postal_code)
        {
            text += ' ' + address.postal_code
        }

        if (address.region)
        {
            text += line_breaks ? line_break : ', '
            text += address.region
        }

        text += line_breaks ? line_break : ', '
        text += address.city

        if (address.country)
        {
            text += line_breaks ? line_break : ' '
            text += address.country
        }

        return text;
    }
}

export interface ClientImage extends Base
{
    file: string;
    width: number;
    height: number;
}